import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button, Checkbox, Input, Modal, Popconfirm, Select } from 'antd'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'
import styled from 'styled-components/macro'
import {
  FoodSafetyHeaderRow,
  FoodSafetyHeroBand,
  FoodSafetyPageTitle,
  FoodSafetySurfaceCard,
} from 'src/components/food-safety/FoodSafetyPageChrome'
import { ollieFabPageClearanceCss } from 'src/components/ollie/ollieFabPlacement'
import VenueHomeOllieJob from './VenueHomeOllieJob'
import LitePlatformDiscovery from 'src/components/subscriptions/LitePlatformDiscovery'

const monthDayOptionLabel = day => {
  const mod = day % 100
  const suffix =
    mod >= 11 && mod <= 13
      ? 'th'
      : { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th'
  return `${day}${suffix}`
}

const MonthDayField = ({ value, onChange, testId }) => (
  <>
    <MonthDayLabel>Day of the month</MonthDayLabel>
    <Select
      value={value}
      onChange={next => onChange(Number(next))}
      data-testid={testId}
      style={{ minWidth: '6rem' }}
      aria-label='Day of the month'
    >
      {Array.from({ length: 31 }, (_, index) => index + 1).map(day => (
        <Select.Option key={day} value={day}>
          {monthDayOptionLabel(day)}
        </Select.Option>
      ))}
    </Select>
    <MonthDayHint>
      If the month doesn’t have this day, it’s due on the last day instead.
    </MonthDayHint>
  </>
)

const SortableTile = ({ id, active, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled: !active,
  })
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.7 : 1,
        cursor: active ? 'grab' : 'default',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
      {...(active ? { ...attributes, ...listeners } : {})}
    >
      {children}
    </div>
  )
}
import OllieAskLink from 'src/components/ollie/OllieAskLink'
import FadeIn from 'src/components/styled/FadeIn'
import useOllie from 'src/hooks/useOllie'
import useOllieAtLimit from 'src/hooks/useOllieAtLimit'
import useUserRole from 'src/hooks/useUserRole'
import ShareWithGroupButton from 'src/screens/menu-builder/components/ShareWithGroupButton'
import { useVenueCustomRoles } from 'src/hooks/useIsBlocked'
import { canManageAnyVenue } from 'src/utils/adminAccess'
import { hasCapability } from 'src/utils/venueRoleCapabilities'
import VenueGroupMembershipChips from 'src/screens/venues/VenueGroupMembershipChips'
import { API } from 'src/utils/interceptor'
import { FOOD_SAFETY_SECONDARY_TEXT } from 'src/constants/foodSafety'
import {
  createVenueHomeChecklist,
  fetchVenueHomeChecklist,
  deleteVenueHomeChecklist,
  shareVenueHomeChecklist,
  fetchVenueHome,
  fetchVenueHomeStats,
  moveVenueHomeWidget,
  restoreVenueHomeWidgets,
  saveVenueHomeWidgets,
  toggleVenueHomeWidget,
  venueHomeShortcuts,
  venueHomeWidgetDraft,
  tickVenueHomeChecklist,
  updateVenueHomeChecklist,
  VENUE_HOME_CHECKLIST_WEEKDAYS,
  venueHomeChecklistItemId,
  venueHomeChecklistNote,
  VENUE_HOME_CHANGED_EVENT,
  venueHomeActableTasks,
  venueHomeStatsPath,
  venueHomeTaskStillOpen,
  venueHomeCategoryFilters,
  venueHomeForRoute,
  venueHomeIsClearDay,
  venueHomeShowAskOllie,
  VENUE_HOME_ASK_OLLIE_LABEL,
  venueHomeTaskCard,
  venueHomeTasksLeadTitle,
  formatVenueHomeBadgeA11yLabel,
  formatVenueHomeBadgeLabel,
  venueHomeTasksAfterRefresh,
  venueHomeWarnings,
} from 'src/utils/venueHomeApi'

const HOME_TASK_SHARE_ROLES = [
  'VENUE_ADMIN',
  'HEAD_CHEF_GM',
  'CHEF',
  'ORDER_ONLY',
  'KITCHEN_PORTER',
]

const venueTitleForId = (venue, venueId) => {
  const title = String(venue?.title || '').trim()
  if (!title || String(venue?._id || '') !== String(venueId || '')) return ''
  return title
}

/**
 * Name of the venue in the URL. A venue still loaded from another site is ignored
 * so a switch never labels this list with the previous kitchen.
 */
export function venueHomeHeading(venue, venueId, venues = []) {
  const loaded = venueTitleForId(venue, venueId)
  if (loaded) return loaded
  const listed = (venues || []).find(item => venueTitleForId(item, venueId))
  return venueTitleForId(listed, venueId) || 'Home'
}

const formatHomeMoney = value => {
  const amount = Number(value)
  const shown = Number.isFinite(amount) ? amount : 0
  return `£${shown.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

const VenueHome = ({ match }) => {
  const venueId = match?.params?.venueId
  const venue = useSelector(state => state.venue?.venue)
  const venues = useSelector(state => state.auth?.account?.venues)
  const account = useSelector(state => state.auth?.account)
  const canSeeGroups = canManageAnyVenue(account)
  const history = useHistory()
  const role = useUserRole()
  const ollie = useOllie()
  const ollieAtLimit = useOllieAtLimit()
  const customRoles = useVenueCustomRoles()
  const [venueGroups, setVenueGroups] = useState([])
  const [category, setCategory] = useState('all')
  const [checklistOpen, setChecklistOpen] = useState(false)
  const [manageOpen, setManageOpen] = useState(false)
  const [manageAdding, setManageAdding] = useState(false)
  const [managedItems, setManagedItems] = useState([])
  const [manageStatus, setManageStatus] = useState('idle')
  const [manageError, setManageError] = useState('')
  const [home, setHome] = useState(null)
  const [kitchenJobText, setKitchenJobText] = useState('')
  const [status, setStatus] = useState('loading')
  const [refreshing, setRefreshing] = useState(false)
  const [checklistTitle, setChecklistTitle] = useState('')
  const [checklistNote, setChecklistNote] = useState('')
  const [checklistCadence, setChecklistCadence] = useState('daily')
  const [checklistWeekday, setChecklistWeekday] = useState('monday')
  const [checklistMonthDay, setChecklistMonthDay] = useState(1)
  const [checklistError, setChecklistError] = useState('')
  const [addingChecklist, setAddingChecklist] = useState(false)
  const [editingChecklistId, setEditingChecklistId] = useState('')
  const [sharingChecklistId, setSharingChecklistId] = useState('')
  const [shareVenueIds, setShareVenueIds] = useState([])
  const [editTitle, setEditTitle] = useState('')
  const [editNote, setEditNote] = useState('')
  const [editCadence, setEditCadence] = useState('daily')
  const [editWeekday, setEditWeekday] = useState('monday')
  const [editMonthDay, setEditMonthDay] = useState(1)
  const [checklistActionError, setChecklistActionError] = useState('')
  const [checklistActionErrorId, setChecklistActionErrorId] = useState('')
  const [checklistBusyId, setChecklistBusyId] = useState('')
  const [statsGlance, setStatsGlance] = useState(null)
  const [statsStatus, setStatsStatus] = useState('idle')
  const [widgetsOpen, setWidgetsOpen] = useState(false)
  const [arranging, setArranging] = useState(false)
  const [boardView, setBoardView] = useState(null)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  )
  const [widgetDraft, setWidgetDraft] = useState([])
  const [widgetsSaving, setWidgetsSaving] = useState(false)
  const [widgetsError, setWidgetsError] = useState('')
  const requestId = useRef(0)
  const homeRef = useRef(null)
  homeRef.current = home

  const loadHome = useCallback(
    (mode = 'load') => {
      const requestedVenueId = venueId
      const id = requestId.current + 1
      requestId.current = id
      if (!requestedVenueId) {
        setHome(null)
        setStatus('error')
        setRefreshing(false)
        return
      }
      if (mode === 'refresh') setRefreshing(true)
      else {
        setHome(null)
        setStatus('loading')
      }
      return fetchVenueHome({ venueId: requestedVenueId })
        .then(data => {
          if (requestId.current !== id) return null
          const scoped = venueHomeForRoute({
            venueId: requestedVenueId,
            requestedVenueId,
            home: data,
          })
          if (!scoped && mode === 'refresh') return null
          if (scoped) {
            scoped.tasks = venueHomeTasksAfterRefresh(
              homeRef.current?.tasks,
              scoped.tasks,
            )
          }
          setHome(scoped)
          setStatus(scoped ? 'ready' : 'error')
          return null
        })
        .catch(() => {
          if (requestId.current !== id) return
          if (mode === 'refresh') return
          setHome(null)
          setStatus('error')
        })
        .finally(() => {
          if (requestId.current === id) setRefreshing(false)
        })
    },
    [venueId],
  )

  useEffect(() => {
    loadHome('load')
  }, [loadHome])

  useEffect(() => {
    if (!canSeeGroups || !venueId) {
      setVenueGroups([])
      return undefined
    }
    let cancelled = false
    API.get('api2', `venue-groups?venueId=${encodeURIComponent(venueId)}`)
      .then(response => {
        if (!cancelled) setVenueGroups(response?.data || [])
        return null
      })
      .catch(() => {
        if (!cancelled) setVenueGroups([])
        return null
      })
    return () => {
      cancelled = true
    }
  }, [canSeeGroups, venueId])

  const venueMatches =
    !home || String(home.venueId || '') === String(venueId || '')
  const shownHome = venueMatches ? home : null
  const shownStatus = venueMatches ? status : 'loading'
  const statsPath = venueHomeStatsPath(shownHome, venueId)
  useEffect(() => {
    if (!statsPath || !venueId) {
      setStatsGlance(null)
      setStatsStatus('idle')
      return undefined
    }
    const end = new Date()
    const start = new Date(end.getFullYear(), end.getMonth(), 1)
    const format = date => {
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${date.getFullYear()}-${month}-${day}`
    }
    let cancelled = false
    setStatsStatus('loading')
    fetchVenueHomeStats({
      venueId,
      from: format(start),
      to: format(end),
    })
      .then(glance => {
        if (cancelled) return null
        setStatsGlance(glance)
        setStatsStatus(glance ? 'ready' : 'error')
        return null
      })
      .catch(() => {
        if (cancelled) return
        setStatsGlance(null)
        setStatsStatus('error')
      })
    return () => {
      cancelled = true
    }
  }, [statsPath, venueId])

  useEffect(() => {
    const refresh = () => loadHome('refresh')
    window.addEventListener(VENUE_HOME_CHANGED_EVENT, refresh)
    return () => window.removeEventListener(VENUE_HOME_CHANGED_EVENT, refresh)
  }, [loadHome])

  const openTask = task => {
    const card = venueHomeTaskCard(task, venueId)
    if (!card.action.to || refreshing) return
    const requestedVenueId = venueId
    const id = requestId.current + 1
    requestId.current = id
    setRefreshing(true)
    return fetchVenueHome({ venueId: requestedVenueId })
      .then(data => {
        if (requestId.current !== id) return null
        const scoped = venueHomeForRoute({
          venueId: requestedVenueId,
          requestedVenueId,
          home: data,
        })
        if (!scoped) return null
        setHome(scoped)
        setStatus('ready')
        if (venueHomeTaskStillOpen(scoped, task.id))
          history.push(card.action.to)
        return null
      })
      .catch(() => {
        if (requestId.current !== id) return
      })
      .finally(() => {
        if (requestId.current === id) setRefreshing(false)
      })
  }

  const loadManagedChecklist = () => {
    setManageStatus('loading')
    setManageError('')
    return fetchVenueHomeChecklist({ venueId })
      .then(items => {
        setManagedItems(items)
        setManageStatus('ready')
        return null
      })
      .catch(error => {
        setManagedItems([])
        setManageStatus('error')
        setManageError(error?.message || 'Could not load your custom tasks.')
      })
  }

  const openManage = () => {
    setManageOpen(true)
    setManageAdding(false)
    setEditingChecklistId('')
    loadManagedChecklist()
  }

  const addChecklistItem = ({ inManage = false } = {}) => {
    const title = checklistTitle.trim()
    if (!title) {
      setChecklistError('Enter what needs doing.')
      return
    }
    setChecklistError('')
    setAddingChecklist(true)
    return createVenueHomeChecklist({
      venueId,
      title,
      note: checklistNote,
      cadence: checklistCadence,
      weekday: checklistWeekday,
      monthDay: checklistMonthDay,
    })
      .then(() => {
        setChecklistTitle('')
        setChecklistNote('')
        setChecklistCadence('daily')
        setChecklistError('')
        setChecklistOpen(false)
        setManageAdding(false)
        loadHome('refresh')
        if (inManage) return loadManagedChecklist()
        return null
      })
      .catch(error => {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Could not add that checklist item.'
        setChecklistError(message)
      })
      .finally(() => setAddingChecklist(false))
  }

  const runChecklistAction = (itemId, action) => {
    setChecklistActionError('')
    setChecklistBusyId(itemId)
    return action()
      .then(() => {
        setEditingChecklistId('')
        loadHome('refresh')
        return null
      })
      .catch(error => {
        setChecklistActionErrorId(itemId)
        setChecklistActionError(
          error?.message || 'Could not update that checklist item.',
        )
      })
      .finally(() => setChecklistBusyId(''))
  }

  const applyWidgets = widgets => {
    setHome(current => (current ? { ...current, widgets } : current))
    setWidgetDraft(venueHomeWidgetDraft(widgets))
  }

  const saveWidgets = () => {
    setWidgetsSaving(true)
    setWidgetsError('')
    return saveVenueHomeWidgets({ venueId, widgetIds: widgetDraft })
      .then(widgets => {
        applyWidgets(widgets)
        setWidgetsOpen(false)
        setArranging(false)
        return null
      })
      .catch(error => {
        setWidgetsError(
          error?.response?.data?.message ||
            error?.message ||
            'Could not save widgets.',
        )
      })
      .finally(() => setWidgetsSaving(false))
  }

  const restoreWidgets = () => {
    setWidgetsSaving(true)
    setWidgetsError('')
    return restoreVenueHomeWidgets({ venueId })
      .then(widgets => {
        applyWidgets(widgets)
        setWidgetsOpen(false)
        return null
      })
      .catch(error => {
        setWidgetsError(
          error?.response?.data?.message ||
            error?.message ||
            'Could not restore the default widgets.',
        )
      })
      .finally(() => setWidgetsSaving(false))
  }

  const widgetChoices = shownHome?.widgetChoices || []
  const widgetRows = [
    ...widgetDraft
      .map(id => widgetChoices.find(choice => choice.id === id))
      .filter(Boolean),
    ...widgetChoices.filter(choice => !widgetDraft.includes(choice.id)),
  ]

  const startChecklistEdit = task => {
    setEditingChecklistId(venueHomeChecklistItemId(task))
    setEditTitle(task.title || '')
    setEditNote(venueHomeChecklistNote(task))
    setEditCadence(
      task.cadence === 'weekly'
        ? 'weekly'
        : task.cadence === 'monthly'
        ? 'monthly'
        : 'daily',
    )
    setEditWeekday(task.weekday || 'monday')
    setEditMonthDay(task.monthDay || 1)
    setChecklistActionError('')
  }

  const canAct = capability => hasCapability(role, capability, customRoles)
  const tasks =
    shownStatus === 'ready' && shownHome
      ? venueHomeActableTasks(shownHome.tasks || [], canAct, role)
      : []
  const filters = venueHomeCategoryFilters(tasks)
  const activeCategory = filters.some(item => item.category === category)
    ? category
    : 'all'
  const inCategory =
    activeCategory === 'all'
      ? tasks
      : tasks.filter(task => task.category === activeCategory)
  const shownTasks = inCategory
  const nextTask =
    shownStatus === 'ready'
      ? tasks.find(task => task.id === shownHome?.nextTaskId) || tasks[0]
      : null
  const warnings =
    shownStatus === 'ready' && shownHome ? venueHomeWarnings(shownHome) : []
  const caughtUp =
    shownStatus === 'ready' &&
    venueHomeIsClearDay({
      tasks,
      warnings: warnings.map(message => ({ message })),
    })
  const layoutIds = arranging
    ? widgetDraft
    : (shownHome?.widgets || []).map(widget => widget.id)
  const showOmsTasks = layoutIds.includes('omsTasks')
  const showCustomTasks = layoutIds.includes('customTasks')
  const orderedTasks = [
    ...(showCustomTasks
      ? shownTasks.filter(task => task.category === 'checklist')
      : []),
    ...(showOmsTasks
      ? shownTasks.filter(task => task.category !== 'checklist')
      : []),
  ]
  const taskTotal = orderedTasks.length
  const taskNumber = task => {
    const index = orderedTasks.findIndex(item => item.id === task.id)
    return index < 0 ? 0 : index + 1
  }
  const displayIds = layoutIds.filter((id, index, list) => {
    if (id !== 'omsTasks' && id !== 'customTasks') return true
    return (
      list.findIndex(item => item === 'omsTasks' || item === 'customTasks') ===
      index
    )
  })
  const shortcutsById = Object.fromEntries(
    venueHomeShortcuts(shownHome, venueId).map(shortcut => [
      shortcut.id,
      shortcut,
    ]),
  )
  return (
    <Wrapper data-testid='venue-home'>
      <FoodSafetyHeroBand>
        <FoodSafetyHeaderRow>
          <div>
            <FoodSafetyPageTitle data-testid='venue-home-title'>
              Home
            </FoodSafetyPageTitle>
            <HeroIntro>
              The starting point for this venue. Today’s job, open tasks, and
              the tools you use most.
            </HeroIntro>
            {canSeeGroups ? (
              <VenueGroupMembershipChips
                groups={venueGroups}
                data-testid='venue-home-group-chips'
              />
            ) : null}
            {nextTask &&
            venueHomeShowAskOllie({
              available: ollie.isAvailable,
              atLimit: ollieAtLimit,
            }) ? (
              <VenueHomeOllieBoundary>
                <OllieAskLink
                  question={
                    shownHome?.askOllie?.question ||
                    'What needs my attention today?'
                  }
                  label={VENUE_HOME_ASK_OLLIE_LABEL}
                  quiet
                  data-testid='venue-home-ask-ollie'
                />
              </VenueHomeOllieBoundary>
            ) : null}
          </div>
          <HeroActions>
            {(shownHome?.widgetChoices || []).length > 0 ? (
              <Button
                data-testid='venue-home-widgets'
                onClick={() => {
                  if (arranging) {
                    saveWidgets()
                    return
                  }
                  setBoardView(null)
                  setWidgetDraft(venueHomeWidgetDraft(shownHome?.widgets))
                  setWidgetsError('')
                  setArranging(true)
                }}
              >
                {arranging ? 'Save layout' : 'Customise'}
              </Button>
            ) : null}
          </HeroActions>
        </FoodSafetyHeaderRow>
      </FoodSafetyHeroBand>
      {boardView ? (
        <>
          <NavRow>
            <BackLink type='button' onClick={() => setBoardView(null)}>
              Back to Home
            </BackLink>
            <QuietButton
              type='button'
              onClick={() => loadHome('refresh')}
              disabled={refreshing || !venueId}
            >
              {refreshing ? 'Refreshing' : 'Refresh tasks'}
            </QuietButton>
          </NavRow>
          <WorkColumn>
            {shownStatus === 'ready' ? (
              <>
                {caughtUp ? (
                  <HeroIntro data-testid='venue-home-caught-up'>
                    You’re caught up. Nothing is outstanding at this venue.
                  </HeroIntro>
                ) : null}
                <TaskStats>
                  <TaskStat>
                    <TaskStatValue>
                      {shownHome?.taskStats?.today || 0}
                    </TaskStatValue>
                    <TaskStatLabel>Done today</TaskStatLabel>
                  </TaskStat>
                  <TaskStat>
                    <TaskStatValue>
                      {shownHome?.taskStats?.week || 0}
                    </TaskStatValue>
                    <TaskStatLabel>This week</TaskStatLabel>
                  </TaskStat>
                  <TaskStat>
                    <TaskStatValue>
                      {shownHome?.taskStats?.total || 0}
                    </TaskStatValue>
                    <TaskStatLabel>Total</TaskStatLabel>
                  </TaskStat>
                </TaskStats>
              </>
            ) : null}
            {filters.length > 1 ? (
              <ChipRow data-testid='venue-home-filters'>
                <FilterChip
                  type='button'
                  $on={activeCategory === 'all'}
                  onClick={() => setCategory('all')}
                >
                  All <ChipCount>{tasks.length}</ChipCount>
                </FilterChip>
                {filters.map(item => (
                  <FilterChip
                    key={item.category}
                    type='button'
                    $on={activeCategory === item.category}
                    onClick={() => setCategory(item.category)}
                  >
                    {item.label} <ChipCount>{item.count}</ChipCount>
                  </FilterChip>
                ))}
              </ChipRow>
            ) : null}
            {warnings.length > 0 ? (
              <FoodSafetySurfaceCard data-testid='venue-home-warnings'>
                {warnings.map(message => (
                  <CardLine key={message}>{message}</CardLine>
                ))}
              </FoodSafetySurfaceCard>
            ) : null}
            {shownStatus === 'loading' || shownStatus === 'error' ? (
              <FoodSafetySurfaceCard data-testid='venue-home-card'>
                {shownStatus === 'loading' ? (
                  <CardLine>Loading this venue’s work.</CardLine>
                ) : null}
                {shownStatus === 'error' ? (
                  <CardLine>This venue’s work could not be loaded.</CardLine>
                ) : null}
              </FoodSafetySurfaceCard>
            ) : null}
            {shownStatus === 'ready' &&
            showCustomTasks &&
            boardView === 'tasks' ? (
              <QuietAdd>
                {checklistOpen ? (
                  <ChecklistPanel>
                    <ChecklistHeading>Add a custom task</ChecklistHeading>
                    <Input
                      value={checklistTitle}
                      placeholder='What needs doing'
                      maxLength={120}
                      onChange={event => setChecklistTitle(event.target.value)}
                      data-testid='venue-home-checklist-title'
                    />
                    <Input
                      value={checklistNote}
                      placeholder='Note (optional)'
                      maxLength={200}
                      onChange={event => setChecklistNote(event.target.value)}
                      data-testid='venue-home-checklist-note'
                    />
                    <ChipRow>
                      <FilterChip
                        type='button'
                        $on={checklistCadence === 'daily'}
                        onClick={() => setChecklistCadence('daily')}
                      >
                        Daily
                      </FilterChip>
                      <FilterChip
                        type='button'
                        $on={checklistCadence === 'weekly'}
                        onClick={() => setChecklistCadence('weekly')}
                      >
                        Weekly
                      </FilterChip>
                      <FilterChip
                        type='button'
                        $on={checklistCadence === 'monthly'}
                        onClick={() => setChecklistCadence('monthly')}
                      >
                        Monthly
                      </FilterChip>
                      {checklistCadence === 'weekly' ? (
                        <Select
                          value={checklistWeekday}
                          onChange={setChecklistWeekday}
                          data-testid='venue-home-checklist-weekday'
                          style={{ minWidth: '9rem' }}
                        >
                          {VENUE_HOME_CHECKLIST_WEEKDAYS.map(day => (
                            <Select.Option key={day} value={day}>
                              {day.charAt(0).toUpperCase() + day.slice(1)}
                            </Select.Option>
                          ))}
                        </Select>
                      ) : null}
                      {checklistCadence === 'monthly' ? (
                        <MonthDayField
                          value={checklistMonthDay}
                          onChange={setChecklistMonthDay}
                          testId='venue-home-checklist-month-day'
                        />
                      ) : null}
                    </ChipRow>
                    {checklistError ? (
                      <CardLine>{checklistError}</CardLine>
                    ) : null}
                    <ChecklistActions>
                      <QuietButton
                        type='button'
                        onClick={() => addChecklistItem()}
                        disabled={addingChecklist}
                        data-testid='venue-home-checklist-add'
                      >
                        {addingChecklist
                          ? 'Adding'
                          : 'Add tasks to my checklist'}
                      </QuietButton>
                      <TextAction
                        type='button'
                        onClick={() => setChecklistOpen(false)}
                      >
                        Cancel
                      </TextAction>
                    </ChecklistActions>
                  </ChecklistPanel>
                ) : (
                  <ChecklistActions>
                    <QuietButton
                      type='button'
                      $green
                      data-testid='venue-home-manage-checklist'
                      onClick={openManage}
                    >
                      Manage custom tasks
                    </QuietButton>
                  </ChecklistActions>
                )}
              </QuietAdd>
            ) : null}
            {shownStatus === 'ready' && showCustomTasks && boardView === 'tasks'
              ? shownTasks
                  .filter(task => task.category === 'checklist')
                  .map(task => {
                    const card = venueHomeTaskCard(task, venueId)
                    const checklistId =
                      task.category === 'checklist'
                        ? venueHomeChecklistItemId(task)
                        : ''
                    const editing =
                      checklistId && editingChecklistId === checklistId
                    return (
                      <TaskCard
                        key={card.id}
                        data-testid='venue-home-task'
                        $stacked={Boolean(editing)}
                      >
                        <TaskCopy>
                          <TaskHeading>
                            <TaskIndex>
                              {taskNumber(task)} of {taskTotal}
                            </TaskIndex>
                            <TaskTitle>{card.title}</TaskTitle>
                          </TaskHeading>
                          {card.detail ? (
                            <TaskDetail>{card.detail}</TaskDetail>
                          ) : null}
                          <TaskMeta>
                            <span>{card.category}</span>
                            {card.due ? <span>{card.due}</span> : null}
                          </TaskMeta>
                        </TaskCopy>
                        {checklistId ? (
                          <ChecklistForm>
                            {editing ? (
                              <>
                                <Input
                                  value={editTitle}
                                  maxLength={120}
                                  onChange={event =>
                                    setEditTitle(event.target.value)
                                  }
                                />
                                <Input
                                  value={editNote}
                                  placeholder='Note (optional)'
                                  maxLength={200}
                                  onChange={event =>
                                    setEditNote(event.target.value)
                                  }
                                />
                                <ChipRow>
                                  <FilterChip
                                    type='button'
                                    $on={editCadence === 'daily'}
                                    onClick={() => setEditCadence('daily')}
                                  >
                                    Daily
                                  </FilterChip>
                                  <FilterChip
                                    type='button'
                                    $on={editCadence === 'weekly'}
                                    onClick={() => setEditCadence('weekly')}
                                  >
                                    Weekly
                                  </FilterChip>
                                  <FilterChip
                                    type='button'
                                    $on={editCadence === 'monthly'}
                                    onClick={() => setEditCadence('monthly')}
                                  >
                                    Monthly
                                  </FilterChip>
                                  {editCadence === 'weekly' ? (
                                    <Select
                                      value={editWeekday}
                                      onChange={setEditWeekday}
                                      style={{ minWidth: '9rem' }}
                                    >
                                      {VENUE_HOME_CHECKLIST_WEEKDAYS.map(
                                        day => (
                                          <Select.Option key={day} value={day}>
                                            {day.charAt(0).toUpperCase() +
                                              day.slice(1)}
                                          </Select.Option>
                                        ),
                                      )}
                                    </Select>
                                  ) : null}
                                  {editCadence === 'monthly' ? (
                                    <MonthDayField
                                      value={editMonthDay}
                                      onChange={setEditMonthDay}
                                    />
                                  ) : null}
                                </ChipRow>
                                <ChecklistActions>
                                  <QuietButton
                                    type='button'
                                    disabled={checklistBusyId === checklistId}
                                    onClick={() => {
                                      if (!editTitle.trim()) {
                                        setChecklistActionError(
                                          'Enter what needs doing.',
                                        )
                                        return
                                      }
                                      runChecklistAction(checklistId, () =>
                                        updateVenueHomeChecklist({
                                          venueId,
                                          itemId: checklistId,
                                          title: editTitle,
                                          note: editNote,
                                          cadence: editCadence,
                                          weekday: editWeekday,
                                          monthDay: editMonthDay,
                                        }),
                                      )
                                    }}
                                  >
                                    {checklistBusyId === checklistId
                                      ? 'Saving'
                                      : 'Save'}
                                  </QuietButton>
                                  <TextAction
                                    type='button'
                                    onClick={() => setEditingChecklistId('')}
                                  >
                                    Cancel
                                  </TextAction>
                                </ChecklistActions>
                              </>
                            ) : (
                              <ChecklistActions>
                                {HOME_TASK_SHARE_ROLES.includes(role) && (
                                  <ShareWithGroupButton
                                    offer={{
                                      kind: 'homeTask',
                                      venueId,
                                      itemId: checklistId,
                                      role,
                                    }}
                                    label='Share task with group…'
                                  />
                                )}
                                <QuietButton
                                  type='button'
                                  disabled={checklistBusyId === checklistId}
                                  onClick={() =>
                                    runChecklistAction(checklistId, () =>
                                      tickVenueHomeChecklist({
                                        venueId,
                                        itemId: checklistId,
                                      }),
                                    )
                                  }
                                >
                                  {checklistBusyId === checklistId
                                    ? 'Saving'
                                    : 'Done for today'}
                                </QuietButton>
                                <TextAction
                                  type='button'
                                  onClick={() => startChecklistEdit(task)}
                                >
                                  Edit
                                </TextAction>
                                <Popconfirm
                                  title='Delete this checklist item?'
                                  okText='Delete'
                                  cancelText='Cancel'
                                  onConfirm={() =>
                                    runChecklistAction(checklistId, () =>
                                      deleteVenueHomeChecklist({
                                        venueId,
                                        itemId: checklistId,
                                      }),
                                    )
                                  }
                                >
                                  <TextAction type='button'>Delete</TextAction>
                                </Popconfirm>
                              </ChecklistActions>
                            )}
                            {checklistActionError &&
                            checklistActionErrorId === checklistId ? (
                              <CardLine>{checklistActionError}</CardLine>
                            ) : null}
                          </ChecklistForm>
                        ) : (
                          <QuietButton
                            type='button'
                            disabled={!card.action.to || refreshing}
                            onClick={() => openTask(task)}
                          >
                            {card.action.label}
                          </QuietButton>
                        )}
                      </TaskCard>
                    )
                  })
              : null}
            {shownStatus === 'ready' && showOmsTasks && boardView === 'tasks'
              ? shownTasks
                  .filter(task => task.category !== 'checklist')
                  .map((task, index, list) => {
                    const card = venueHomeTaskCard(task, venueId)
                    const firstOfKind =
                      list.findIndex(
                        item => item.category === task.category,
                      ) === index
                    const groupReason = !firstOfKind
                      ? ''
                      : task.category === 'goodsReceipting'
                      ? 'Report a short or wrong delivery and claim the credit.'
                      : task.category === 'unmatchedInvoice'
                      ? 'Match these so your orders and figures agree with the bill.'
                      : ''
                    return (
                      <React.Fragment key={card.id}>
                        {groupReason ? (
                          <GroupReason>{groupReason}</GroupReason>
                        ) : null}
                        <TaskCard data-testid='venue-home-task'>
                          <TaskCopy>
                            <TaskHeading>
                              <TaskIndex>
                                {taskNumber(task)} of {taskTotal}
                              </TaskIndex>
                              <TaskTitle>{card.title}</TaskTitle>
                            </TaskHeading>
                            {card.detail ? (
                              <TaskDetail>{card.detail}</TaskDetail>
                            ) : null}
                            <TaskMeta>
                              <span>{card.category}</span>
                              {card.due ? <span>{card.due}</span> : null}
                            </TaskMeta>
                          </TaskCopy>
                          <QuietButton
                            type='button'
                            disabled={!card.action.to || refreshing}
                            onClick={() => openTask(task)}
                          >
                            {task.actionLabel || card.action.label}
                          </QuietButton>
                        </TaskCard>
                      </React.Fragment>
                    )
                  })
              : null}
          </WorkColumn>
        </>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={({ active, over }) => {
            if (!over || active.id === over.id) return
            setWidgetDraft(current => {
              const from = current.indexOf(active.id)
              const to = current.indexOf(over.id)
              if (from < 0 || to < 0) return current
              const moved = arrayMove(current, from, to)
              const taskIds = ['omsTasks', 'customTasks'].filter(id =>
                moved.includes(id),
              )
              if (taskIds.length < 2 || !taskIds.includes(active.id))
                return moved
              const without = moved.filter(
                id => !taskIds.includes(id) || id === active.id,
              )
              const at = without.indexOf(active.id)
              without.splice(
                at + 1,
                0,
                ...taskIds.filter(id => id !== active.id),
              )
              return without
            })
          }}
        >
          <SortableContext items={displayIds} strategy={rectSortingStrategy}>
            <HomeGrid>
              {displayIds.map(id => {
                let body = null
                if (id === 'ollie') {
                  body = (
                    <OllieSlot>
                      <VenueHomeOllieJob
                        venueId={venueId}
                        currentPlan={venue?.usageLimits?.currentPlan}
                        onJobText={setKitchenJobText}
                      />
                      {arranging ? (
                        <ShortcutText>Drag to move</ShortcutText>
                      ) : null}
                    </OllieSlot>
                  )
                } else if (id === 'omsTasks' || id === 'customTasks') {
                  const nextTitle = venueHomeTasksLeadTitle(
                    tasks,
                    kitchenJobText,
                  )
                  const taskBadge = formatVenueHomeBadgeLabel(
                    shownHome?.summary?.outstanding,
                  )
                  body = (
                    <ShortcutCard
                      as='button'
                      type='button'
                      onClick={() => !arranging && setBoardView('tasks')}
                    >
                      <ShortcutHead>
                        <ShortcutTitle>Tasks</ShortcutTitle>
                        {taskBadge ? (
                          <TaskCount
                            data-testid='venue-home-tasks-badge'
                            aria-label={formatVenueHomeBadgeA11yLabel(
                              shownHome?.summary?.outstanding,
                            )}
                          >
                            {taskBadge}
                          </TaskCount>
                        ) : null}
                      </ShortcutHead>
                      <ShortcutText>
                        {tasks.length
                          ? `${tasks.length} outstanding.${
                              nextTitle ? ` ${nextTitle}` : ''
                            }`
                          : 'See outstanding OMS tasks, or create your own list.'}
                      </ShortcutText>
                      {arranging ? (
                        <ShortcutText>Drag to move</ShortcutText>
                      ) : (
                        <ShortcutCta>See all</ShortcutCta>
                      )}
                    </ShortcutCard>
                  )
                } else if (
                  id === 'stats' &&
                  statsPath &&
                  statsStatus !== 'idle'
                ) {
                  body = (
                    <StatsBar data-testid='venue-home-stats'>
                      <StatsHeading>This month</StatsHeading>
                      {statsStatus === 'loading' ? (
                        <CardLine>Loading this month’s figures.</CardLine>
                      ) : null}
                      {statsStatus === 'error' ? (
                        <CardLine>These figures could not be loaded.</CardLine>
                      ) : null}
                      {statsStatus === 'ready' && statsGlance ? (
                        <StatsFigures>
                          <StatsFigure>
                            <StatsValue>
                              {formatHomeMoney(statsGlance.sales)}
                            </StatsValue>
                            <StatsLabel>Sales</StatsLabel>
                          </StatsFigure>
                          <StatsFigure>
                            <StatsValue>
                              {formatHomeMoney(statsGlance.omsSpend)}
                            </StatsValue>
                            <StatsLabel>OMS spend, ex VAT</StatsLabel>
                          </StatsFigure>
                          <StatsFigure>
                            <StatsValue
                              $tone={
                                statsGlance.gp == null
                                  ? ''
                                  : parseFloat(statsGlance.gp) >= 75
                                  ? 'good'
                                  : 'attention'
                              }
                            >
                              {statsGlance.gp == null
                                ? '—'
                                : `${statsGlance.gp}%`}
                            </StatsValue>
                            <StatsLabel>Gross profit</StatsLabel>
                          </StatsFigure>
                        </StatsFigures>
                      ) : null}
                      {arranging ? (
                        <ShortcutText>Drag to move</ShortcutText>
                      ) : (
                        <StatsLink to={statsPath}>Open gross profit</StatsLink>
                      )}
                    </StatsBar>
                  )
                } else if (shortcutsById[id]) {
                  const shortcut = shortcutsById[id]
                  body = (
                    <ShortcutCard>
                      <ShortcutTitle>{shortcut.label}</ShortcutTitle>
                      {shortcut.subtitle ? (
                        <ShortcutText>{shortcut.subtitle}</ShortcutText>
                      ) : null}
                      {arranging ? (
                        <ShortcutText>Drag to move</ShortcutText>
                      ) : (
                        <>
                          <ShortcutLink to={shortcut.to}>
                            <ShortcutCta data-testid={shortcut.testId}>
                              {shortcut.cta || shortcut.label}
                            </ShortcutCta>
                          </ShortcutLink>
                          {shortcut.secondaryCta ? (
                            <ShortcutLink to={shortcut.to}>
                              <ShortcutCta $quiet>
                                {shortcut.secondaryCta}
                              </ShortcutCta>
                            </ShortcutLink>
                          ) : null}
                        </>
                      )}
                    </ShortcutCard>
                  )
                }
                if (!body) return null
                return (
                  <SortableTile key={id} id={id} active={arranging}>
                    {body}
                    {arranging ? (
                      <Button
                        type='link'
                        onPointerDown={event => event.stopPropagation()}
                        onClick={() =>
                          setWidgetDraft(current =>
                            current.filter(item =>
                              id === 'omsTasks' || id === 'customTasks'
                                ? item !== 'omsTasks' && item !== 'customTasks'
                                : item !== id,
                            ),
                          )
                        }
                      >
                        Hide
                      </Button>
                    ) : null}
                  </SortableTile>
                )
              })}
            </HomeGrid>
          </SortableContext>
          {arranging ? (
            <>
              <CardLine>
                Drag the cards into the order you want, then Save layout. Hide
                takes a card off Home.
                {widgetsError ? ` ${widgetsError}` : ''}
              </CardLine>
              <FilterRow>
                {!widgetDraft.includes('omsTasks') &&
                !widgetDraft.includes('customTasks') &&
                widgetChoices.some(
                  choice =>
                    choice.id === 'omsTasks' || choice.id === 'customTasks',
                ) ? (
                  <Button
                    onClick={() =>
                      setWidgetDraft(current => [
                        ...current,
                        'omsTasks',
                        'customTasks',
                      ])
                    }
                  >
                    Show Tasks
                  </Button>
                ) : null}
                {widgetChoices
                  .filter(
                    choice =>
                      choice.id !== 'omsTasks' &&
                      choice.id !== 'customTasks' &&
                      !widgetDraft.includes(choice.id),
                  )
                  .map(choice => (
                    <Button
                      key={choice.id}
                      onClick={() =>
                        setWidgetDraft(current => [...current, choice.id])
                      }
                    >
                      Show {choice.label}
                    </Button>
                  ))}
              </FilterRow>
            </>
          ) : null}
        </DndContext>
      )}
      {!boardView && !arranging ? (
        <LitePlatformDiscovery
          venueId={venueId}
          currentPlan={venue?.usageLimits?.currentPlan}
        />
      ) : null}
      <Modal
        title='Manage custom tasks'
        visible={manageOpen}
        onCancel={() => setManageOpen(false)}
        footer={null}
        data-testid='venue-home-manage-modal'
      >
        <CardLine>
          Every custom task for you at this venue, including weekly ones that
          are not due today.
        </CardLine>
        {manageAdding ? (
          <ChecklistPanel>
            <ChecklistHeading>Add a custom task</ChecklistHeading>
            <Input
              value={checklistTitle}
              placeholder='What needs doing'
              maxLength={120}
              onChange={event => setChecklistTitle(event.target.value)}
            />
            <Input
              value={checklistNote}
              placeholder='Note (optional)'
              maxLength={200}
              onChange={event => setChecklistNote(event.target.value)}
            />
            <ChipRow>
              <FilterChip
                type='button'
                $on={checklistCadence === 'daily'}
                onClick={() => setChecklistCadence('daily')}
              >
                Daily
              </FilterChip>
              <FilterChip
                type='button'
                $on={checklistCadence === 'weekly'}
                onClick={() => setChecklistCadence('weekly')}
              >
                Weekly
              </FilterChip>
              <FilterChip
                type='button'
                $on={checklistCadence === 'monthly'}
                onClick={() => setChecklistCadence('monthly')}
              >
                Monthly
              </FilterChip>
              {checklistCadence === 'weekly' ? (
                <Select
                  value={checklistWeekday}
                  onChange={setChecklistWeekday}
                  style={{ minWidth: '9rem' }}
                >
                  {VENUE_HOME_CHECKLIST_WEEKDAYS.map(day => (
                    <Select.Option key={day} value={day}>
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                    </Select.Option>
                  ))}
                </Select>
              ) : null}
              {checklistCadence === 'monthly' ? (
                <MonthDayField
                  value={checklistMonthDay}
                  onChange={setChecklistMonthDay}
                />
              ) : null}
            </ChipRow>
            {checklistError ? <CardLine>{checklistError}</CardLine> : null}
            <ChecklistActions>
              <QuietButton
                type='button'
                disabled={addingChecklist}
                onClick={() => addChecklistItem({ inManage: true })}
              >
                {addingChecklist ? 'Adding' : 'Add tasks to my checklist'}
              </QuietButton>
              <TextAction type='button' onClick={() => setManageAdding(false)}>
                Cancel
              </TextAction>
            </ChecklistActions>
          </ChecklistPanel>
        ) : (
          <TextAction
            type='button'
            onClick={() => {
              setChecklistTitle('')
              setChecklistNote('')
              setChecklistCadence('daily')
              setChecklistError('')
              setManageAdding(true)
            }}
          >
            Add a custom task
          </TextAction>
        )}
        {manageStatus === 'loading' ? (
          <CardLine>Loading your custom tasks.</CardLine>
        ) : null}
        {manageStatus === 'error' ? <CardLine>{manageError}</CardLine> : null}
        {manageStatus === 'ready' && managedItems.length === 0 ? (
          <CardLine>You have not created any custom tasks yet.</CardLine>
        ) : null}
        {managedItems.map(item => (
          <TaskCard key={item.id}>
            {editingChecklistId === item.id ? (
              <ChecklistForm>
                <Input
                  value={editTitle}
                  maxLength={120}
                  onChange={event => setEditTitle(event.target.value)}
                />
                <Input
                  value={editNote}
                  placeholder='Note (optional)'
                  maxLength={200}
                  onChange={event => setEditNote(event.target.value)}
                />
                <ChipRow>
                  <FilterChip
                    type='button'
                    $on={editCadence === 'daily'}
                    onClick={() => setEditCadence('daily')}
                  >
                    Daily
                  </FilterChip>
                  <FilterChip
                    type='button'
                    $on={editCadence === 'weekly'}
                    onClick={() => setEditCadence('weekly')}
                  >
                    Weekly
                  </FilterChip>
                  <FilterChip
                    type='button'
                    $on={editCadence === 'monthly'}
                    onClick={() => setEditCadence('monthly')}
                  >
                    Monthly
                  </FilterChip>
                  {editCadence === 'weekly' ? (
                    <Select
                      value={editWeekday}
                      onChange={setEditWeekday}
                      style={{ minWidth: '9rem' }}
                    >
                      {VENUE_HOME_CHECKLIST_WEEKDAYS.map(day => (
                        <Select.Option key={day} value={day}>
                          {day.charAt(0).toUpperCase() + day.slice(1)}
                        </Select.Option>
                      ))}
                    </Select>
                  ) : null}
                  {editCadence === 'monthly' ? (
                    <MonthDayField
                      value={editMonthDay}
                      onChange={setEditMonthDay}
                    />
                  ) : null}
                </ChipRow>
                <ChecklistActions>
                  <QuietButton
                    type='button'
                    disabled={checklistBusyId === item.id}
                    onClick={() => {
                      if (!editTitle.trim()) {
                        setChecklistActionError('Enter what needs doing.')
                        setChecklistActionErrorId(item.id)
                        return
                      }
                      runChecklistAction(item.id, () =>
                        updateVenueHomeChecklist({
                          venueId,
                          itemId: item.id,
                          title: editTitle,
                          note: editNote,
                          cadence: editCadence,
                          weekday: editWeekday,
                          monthDay: editMonthDay,
                        }).then(() => {
                          setEditingChecklistId('')
                          return loadManagedChecklist()
                        }),
                      )
                    }}
                  >
                    {checklistBusyId === item.id ? 'Saving' : 'Save'}
                  </QuietButton>
                  <TextAction
                    type='button'
                    onClick={() => setEditingChecklistId('')}
                  >
                    Cancel
                  </TextAction>
                </ChecklistActions>
              </ChecklistForm>
            ) : (
              <TaskCopy>
                <TaskTitle>{item.title}</TaskTitle>
                {item.note ? <TaskDetail>{item.note}</TaskDetail> : null}
                <TaskMeta>
                  <span>{item.schedule}</span>
                </TaskMeta>
                <ChecklistActions>
                  {HOME_TASK_SHARE_ROLES.includes(role) && (
                    <ShareWithGroupButton
                      offer={{
                        kind: 'homeTask',
                        venueId,
                        itemId: item.id || item._id,
                        role,
                      }}
                      label='Share task with group…'
                    />
                  )}
                  {sharingChecklistId === item.id ? (
                    <>
                      <Select
                        mode='multiple'
                        showSearch
                        optionFilterProp='children'
                        placeholder='Other venues'
                        value={shareVenueIds}
                        onChange={setShareVenueIds}
                        style={{ minWidth: '16rem' }}
                        aria-label='Venues to share with'
                      >
                        {(venues || [])
                          .filter(
                            choice =>
                              choice?._id &&
                              String(choice._id) !== String(venueId) &&
                              choice.title,
                          )
                          .map(choice => (
                            <Select.Option
                              key={choice._id}
                              value={String(choice._id)}
                            >
                              {choice.title}
                            </Select.Option>
                          ))}
                      </Select>
                      <QuietButton
                        type='button'
                        onClick={() =>
                          runChecklistAction(item.id, () =>
                            shareVenueHomeChecklist({
                              venueId,
                              itemId: item.id,
                              venueIds: shareVenueIds,
                            }).then(result => {
                              const names = (result?.shared || [])
                                .map(row => row.title)
                                .filter(Boolean)
                              setSharingChecklistId('')
                              setShareVenueIds([])
                              setChecklistActionErrorId(item.id)
                              setChecklistActionError(
                                names.length
                                  ? `Shared with ${names.join(', ')}.`
                                  : 'Shared.',
                              )
                              return null
                            }),
                          )
                        }
                      >
                        {checklistBusyId === item.id ? 'Sharing' : 'Share'}
                      </QuietButton>
                      <TextAction
                        type='button'
                        onClick={() => {
                          setSharingChecklistId('')
                          setShareVenueIds([])
                        }}
                      >
                        Cancel
                      </TextAction>
                    </>
                  ) : (
                    <>
                      <TextAction
                        type='button'
                        onClick={() => {
                          setSharingChecklistId('')
                          setEditingChecklistId(item.id)
                          setEditTitle(item.title || '')
                          setEditNote(item.note || '')
                          setEditCadence(
                            item.cadence === 'weekly'
                              ? 'weekly'
                              : item.cadence === 'monthly'
                              ? 'monthly'
                              : 'daily',
                          )
                          setEditMonthDay(item.monthDay || 1)
                          setEditWeekday(item.weekday || 'monday')
                          setChecklistActionError('')
                        }}
                      >
                        Edit
                      </TextAction>
                      {(venues || []).some(
                        choice =>
                          choice?._id && String(choice._id) !== String(venueId),
                      ) ? (
                        <TextAction
                          type='button'
                          onClick={() => {
                            setEditingChecklistId('')
                            setSharingChecklistId(item.id)
                            setShareVenueIds([])
                            setChecklistActionError('')
                          }}
                        >
                          Share
                        </TextAction>
                      ) : null}
                      <Popconfirm
                        title='Delete this checklist item?'
                        okText='Delete'
                        cancelText='Cancel'
                        onConfirm={() =>
                          runChecklistAction(item.id, () =>
                            deleteVenueHomeChecklist({
                              venueId,
                              itemId: item.id,
                            }).then(() => loadManagedChecklist()),
                          )
                        }
                      >
                        <TextAction type='button'>Delete</TextAction>
                      </Popconfirm>
                    </>
                  )}
                </ChecklistActions>
              </TaskCopy>
            )}
            {checklistActionError && checklistActionErrorId === item.id ? (
              <CardLine>{checklistActionError}</CardLine>
            ) : null}
          </TaskCard>
        ))}
      </Modal>
      <Modal
        title='Customise'
        visible={widgetsOpen}
        onCancel={() => setWidgetsOpen(false)}
        onOk={saveWidgets}
        okText='Save'
        confirmLoading={widgetsSaving}
        data-testid='venue-home-widgets-modal'
      >
        <CardLine>
          Customise the page layout. Tick what you want on Home, and move
          shortcuts and stats into the order you use. Untick OMS tasks or custom
          tasks to hide that list.
        </CardLine>
        <ChecklistForm>
          {widgetRows.map(choice => {
            const shown = widgetDraft.includes(choice.id)
            const index = widgetDraft.indexOf(choice.id)
            return (
              <FilterRow key={choice.id}>
                <Checkbox
                  checked={shown}
                  onChange={event =>
                    setWidgetDraft(
                      toggleVenueHomeWidget(
                        widgetDraft,
                        choice.id,
                        event.target.checked,
                      ),
                    )
                  }
                >
                  {choice.label}
                </Checkbox>
                {shown ? (
                  <>
                    <Button
                      disabled={index <= 0}
                      onClick={() =>
                        setWidgetDraft(
                          moveVenueHomeWidget(widgetDraft, choice.id, -1),
                        )
                      }
                    >
                      Up
                    </Button>
                    <Button
                      disabled={index === widgetDraft.length - 1}
                      onClick={() =>
                        setWidgetDraft(
                          moveVenueHomeWidget(widgetDraft, choice.id, 1),
                        )
                      }
                    >
                      Down
                    </Button>
                  </>
                ) : null}
              </FilterRow>
            )
          })}
          {widgetsError ? <CardLine>{widgetsError}</CardLine> : null}
          <Button onClick={restoreWidgets} loading={widgetsSaving}>
            Restore default
          </Button>
        </ChecklistForm>
      </Modal>
    </Wrapper>
  )
}

const OllieSlot = styled.div`
  height: 100%;
  min-height: 11rem;
  display: flex;
  flex-direction: column;

  > :first-child {
    flex: 1;
    height: 100%;
    display: flex;
    flex-direction: column;
    border-radius: 16px;
  }

  [data-testid='nudge-body'] {
    flex: 1;
  }

  [data-testid='venue-home-ollie-job'] > div:last-child {
    margin-top: auto;
  }

  button[data-testid='nudge-cta'] {
    width: 100%;
    height: auto;
    margin-top: 0.75rem;
    border-radius: 20px;
    padding: 0.55rem 1rem;
  }
`

const HomeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-top: 1rem;
  align-items: stretch;
  grid-auto-rows: 1fr;

  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: 1100px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`

const WorkColumn = styled.div`
  min-width: 0;
  flex: 1;
`

const StatsBar = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 11rem;
  padding: 1rem;
  border-radius: 16px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colours.lettuceGreen};
`

const StatsHeading = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`

const StatsFigures = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
  flex: 1;
  align-content: start;
`

const StatsFigure = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`

const StatsValue = styled.span`
  color: ${({ $tone, theme }) =>
    $tone === 'good'
      ? '#52c41a'
      : $tone === 'attention'
      ? '#ff4d4f'
      : theme.colours.bbqBlack};
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
`

const StatsLabel = styled.span`
  font-size: 0.75rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const StatsLink = styled(Link)`
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin-top: auto;
  text-align: center;
  border-radius: 20px;
  padding: 0.55rem 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  background: ${({ theme }) => theme.colours.bbqBlack};
  color: ${({ theme }) => theme.colours.mayoWhite};
  border: 1px solid ${({ theme }) => theme.colours.lettuceGreen};
`

const ShortcutHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`

const TaskCount = styled.span`
  flex: 0 0 auto;
  min-width: 16px;
  padding: 0 6px;
  border-radius: 999px;
  background: #fa8c16;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
`

const ShortcutTitle = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const ShortcutText = styled.p`
  margin: 0.35rem 0 0;
  flex: 1;
  min-height: 2.6rem;
  font-size: 0.85rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours.oliveGrey};
`

const ShortcutLink = styled(Link)`
  display: block;
  width: 100%;
  margin-top: auto;
  padding-top: 0.75rem;
  & + & {
    margin-top: 0.5rem;
    padding-top: 0;
  }
`

const ShortcutCta = styled.span`
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin-top: auto;
  text-align: center;
  border-radius: 20px;
  padding: 0.55rem 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  background: ${({ $quiet, theme }) =>
    $quiet ? theme.colours.lettuceGreen : theme.colours.bbqBlack};
  color: ${({ $quiet, theme }) =>
    $quiet ? theme.colours.bbqBlack : theme.colours.mayoWhite};
  border: 1px solid
    ${({ $quiet, theme }) =>
      $quiet ? theme.colours.bbqBlack : theme.colours.lettuceGreen};
`

const ShortcutCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  min-height: 11rem;
  height: 100%;
  padding: 0.9rem 1rem;
  border-radius: 16px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colours.lettuceGreen};
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: ${({ as }) => (as === 'button' ? 'pointer' : 'default')};
`

const HeroIntro = styled.p`
  margin: ${({ theme }) => theme.spacing.sm} 0 0;
  max-width: 38rem;
  font-size: 0.95rem;
  line-height: 1.45;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

class VenueHomeOllieBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { failed: false }
  }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  render() {
    if (this.state.failed) return null
    return this.props.children
  }
}

const QuietAdd = styled.div`
  margin: 0.15rem 0 0.85rem;
`

const ChecklistPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin: 0 0 0.85rem;
  padding: 0.9rem 1rem;
  border-radius: 16px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colours.lettuceGreen};

  .ant-input,
  .ant-select-selector {
    border-radius: 12px !important;
  }
`

const ChecklistHeading = styled.p`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const NavRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 1rem 0 0.75rem;
`

const TaskStats = styled.div`
  display: flex;
  gap: 0.6rem;
  margin: -0.35rem 0 0.85rem;
`

const TaskStat = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.55rem 0.75rem;
  border-radius: 12px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colours.lettuceGreen};
`

const TaskStatValue = styled.span`
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.1;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const TaskStatLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.oliveGrey};
`

const BackLink = styled.button`
  align-self: flex-start;
  margin: 0;
  padding: 0.45rem 0.95rem;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colours.bbqBlack};
  background: #fff;
  color: ${({ theme }) => theme.colours.bbqBlack};
  font: inherit;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;

  &::before {
    content: '‹ ';
  }
`

const QuietButton = styled.button`
  flex: 0 0 auto;
  border-radius: 999px;
  padding: 0.4rem 0.9rem;
  background: ${({ $green, theme }) =>
    $green ? theme.colours.lettuceGreen : '#fff'};
  color: ${({ theme }) => theme.colours.bbqBlack};
  border: 1px solid
    ${({ $green, theme }) =>
      $green ? theme.colours.bbqBlack : theme.colours.oliveGrey};
  font: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`

const MonthDayLabel = styled.span`
  align-self: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const MonthDayHint = styled.p`
  flex: 1 0 100%;
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours.oliveGrey};
`

const GroupReason = styled.p`
  margin: 0 0 0.75rem;
  color: ${({ theme }) => theme.colours.oliveGrey || '#5c6560'};
  font-size: 0.9rem;
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0 0 0.85rem;
`

const ChipCount = styled.span`
  color: #fa8c16;
`

const FilterChip = styled.button`
  border-radius: 999px;
  padding: 0.35rem 0.8rem;
  background: ${({ $on, theme }) => ($on ? theme.colours.bbqBlack : '#fff')};
  color: ${({ $on, theme }) =>
    $on ? theme.colours.mayoWhite : theme.colours.bbqBlack};
  border: 1px solid
    ${({ $on, theme }) =>
      $on ? theme.colours.bbqBlack : theme.colours.oliveGrey};
  font: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
`

const ChecklistActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 1rem;
`

const TextAction = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colours.oliveGrey};
  font: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 0.18rem;
`

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  align-items: flex-start;
  margin-left: 1.5rem;
`

const ChecklistForm = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 0.5rem;
`

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
`

const CardLine = styled.p`
  margin: 0;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const TaskCard = styled(FoodSafetySurfaceCard)`
  margin-top: ${({ theme }) => theme.spacing.sm};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  ${({ $stacked }) =>
    $stacked
      ? `
    flex-wrap: wrap;
    align-items: stretch;
    ${ChecklistForm} { flex: 1 1 100%; }
  `
      : ''}
`

const TaskCopy = styled.div`
  flex: 1 1 auto;
  min-width: 12rem;
`

const TaskHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`

const TaskIndex = styled.span`
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  font-size: 0.8rem;
  font-weight: 600;
`

const TaskTitle = styled.p`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
`

const TaskDetail = styled.p`
  margin: 0.35rem 0 0;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const TaskMeta = styled.p`
  margin: 0.35rem 0 0;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  font-size: 0.9rem;

  span + span::before {
    content: '·';
    margin: 0 0.4rem;
  }
`

const Wrapper = styled(FadeIn)`
  width: 100%;
  max-width: 72rem;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.sm};
  box-sizing: border-box;
  ${ollieFabPageClearanceCss}

  ${({ theme }) => theme.device('md')`
    padding: ${theme.spacing.md};
    ${ollieFabPageClearanceCss}
  `};
`

export default VenueHome
